import type { APIError, GetRecommendationsQuery } from '@vizit/api';
import type { IdealMixAsset, ImageType, ImageView, OriginalPdpSnapshotView } from '@vizit/api';
import type { PdpDisplayMetrics, PdpScoreStatus, PdpStatus, ScoreBreakdown, ScoreMethod } from '@vizit/api';
import type { PdpBenchmarkScore } from '@vizit/api';
import type { OptimizationRecommendationDataMap } from '@vizit/organisms';
import type { AssetsFilterSource, PendingState } from '@vizit/organisms';
import { createContext, useContext } from 'react';
import type { DragHandlers, DragState } from '../../types/drag-drop';

enum ViewMode {
  EXPERIMENT = 'experiment',
  ORIGINAL = 'original',
}

/**
 * Data structure for the product summary section
 */
interface ProductSummaryData {
  /**
   * The name of the product
   */
  name: PendingState<string>;
  /**
   * The listing score of the product
   */
  listingScore: PendingState<number>;
  /**
   * The score change (difference) of the product
   */
  scoreChange: PendingState<number>;
  /**
   * The status of the score
   */
  scoreStatus: `${PdpScoreStatus}`;
  /**
   * The retailer of the product
   */
  retailer: string;
  /**
   * The ASIN of the product
   */
  itemId: string;
  /**
   * The product URL (computed field)
   */
  productUrl: string | null;
  /**
   * The brand of the product
   */
  brand: PendingState<string>;
  /**
   * The category of the product
   */
  category: PendingState<string>;
  /**
   * The category breadcrumbs from L1 down to the leaf category (from the category's
   * comma-separated `breadcrumbs`). Empty until the category has loaded or when unavailable.
   */
  categoryBreadcrumbs: string[];
  /**
   * The source type of the product (manual or scraped)
   */
  sourceType: 'manual' | 'scraped' | null;
}

/**
 * Data structure for the hero image section
 */
interface HeroImageData extends Partial<ImageView> {
  /**
   * The ID of the hero image
   */
  id: string;
  /**
   * Whether the hero image is loading
   */
  isLoading: boolean;
  /**
   * Whether the hero image is pending
   */
  isPending: boolean;
}

/**
 * Data structure for the carousel section
 */
interface CarouselData {
  /**
   * Array of carousel images
   */
  images: ImageView[];
  /**
   * Whether the carousel is loading
   */
  isLoading: boolean;
  /**
   * Number of loading placeholders to display
   */
  loadingCount: number;
  /**
   * Whether the carousel is pending
   */
  isPending: boolean;
}

/**
 * Data structure for the optimizations section
 */
interface OptimizationsData {
  /**
   * The optimization recommendations data (V1)
   * @deprecated Use recommendationsV2 when PDP_RECOMMENDATIONS_V2 flag is enabled
   */
  recommendations: OptimizationRecommendationDataMap | null;
  /**
   * The new V2 recommendations data (when PDP_RECOMMENDATIONS_V2 flag is enabled)
   */
  recommendationsV2: GetRecommendationsQuery | null;
  /**
   * Whether the optimizations are loading
   */
  isLoading: boolean;
  /**
   * Whether there are any errors in optimizations
   */
  hasError: boolean;
  /**
   * Whether the recommendation metrics are currently polling for updates (v2)
   */
  isPollingForUpdatesV2: boolean;
  /**
   * Whether the V2 recommendations feature flag is enabled
   */
  isV2Enabled: boolean;
  /**
   * Whether the carousel reorder mutation is in progress
   */
  isReorderingCarousel: boolean;
}

/**
 * Main product details context data structure
 */
interface ProductDetailsContextType {
  /**
   * Current active tab from URL
   * @type {'hero' | 'carousel'}
   * @default 'hero' (when absent)
   * Maps to search param `tab`.
   */
  activeTab?: 'hero' | 'carousel';
  /**
   * Current carousel asset-source selection from URL. Drives the V1
   * Imported/Spark sub-tabs (never 'all' there) and the V2 drawer's source
   * filter ('all' shows both sources).
   * @type {AssetsFilterSource}
   * @default 'imported' for the V1 sub-tabs, 'all' for the V2 filter (when absent)
   * Maps to search param `carouselTab`.
   */
  carouselSubTab?: AssetsFilterSource;
  /**
   * Current hero asset-source selection from URL (behind the HERO_SPARK_IMAGES
   * flag). Same semantics as `carouselSubTab`.
   * @type {AssetsFilterSource}
   * @default 'imported' for the V1 sub-tabs, 'all' for the V2 filter (when absent)
   * Maps to search param `heroTab`.
   */
  heroSubTab?: AssetsFilterSource;
  /**
   * Whether the hero tab's assets drawer shows favorited assets only.
   * @type {boolean}
   * @default false
   * Maps to search param `heroFav`.
   */
  heroFavoritesOnly?: boolean;
  /**
   * Whether the carousel tab's assets drawer shows favorited assets only.
   * @type {boolean}
   * @default false
   * Maps to search param `carouselFav`.
   */
  carouselFavoritesOnly?: boolean;
  /**
   * Carousel data
   */
  carousel: CarouselData;
  /**
   * Current drag-and-drop state
   */
  dragState: DragState;
  /**
   * Drag-and-drop event handlers
   */
  dragHandlers: DragHandlers;
  /**
   * Current drawer state from URL
   * @type {boolean}
   * @default false (when absent)
   * Maps to search param `products`.
   */
  isDrawerOpen?: boolean;
  /**
   * Error from the PDP query
   */
  error?: unknown;
  /**
   * Hero image data
   */
  hero: HeroImageData;
  /**
   * Whether the PDP is loading
   *
   * @type {boolean}
   */
  isPdpLoading: boolean;
  /**
   * Whether the PDP is currently polling for updates (score_status not 'SCORED')
   *
   * @type {boolean}
   */
  isPolling: boolean;
  /**
   * The last time the product was refreshed
   *
   * @type {Date | null | undefined}
   */
  lastRefreshed?: Date | null;
  /**
   * The current organization ID
   */
  organizationId: string;
  /**
   * Optimizations data
   */
  optimizations: OptimizationsData;
  /**
   * The values the API resolved for the performance card. Null until the PDP is scored.
   *
   * @type {PdpDisplayMetrics | null}
   */
  displayMetrics: PdpDisplayMetrics | null;
  /**
   * GAM or FORMULA: which method produced the PDP's listing score. Null on the original
   * snapshot view, which never carries formula scoring.
   *
   * @type {ScoreMethod | null}
   */
  scoreMethod: ScoreMethod | null;
  /**
   * Per-component formula breakdown backing the performance card's formula mode. Null
   * when formula scoring is off for the org, the breakdown hasn't landed, or on the
   * original snapshot view.
   *
   * @type {ScoreBreakdown | null}
   */
  scoreBreakdown: ScoreBreakdown | null;
  /**
   * The PDP's target asset mix in rank order, as served by the API.
   *
   * @type {IdealMixAsset[] | null}
   */
  idealMixRanked: IdealMixAsset[] | null;
  /**
   * The live PDP's default benchmark score entry, else its first; never the original snapshot's
   */
  benchmarkScore?: PdpBenchmarkScore | null;
  /**
   * The planned retailer of a page staged for the Other retailer
   */
  customRetailer?: string | null;
  /**
   * The PDP ID for API calls
   */
  pdpId: string;
  /**
   * The portfolio ID for API calls
   */
  workspaceId: string;
  /**
   * The product category ID for API calls
   */
  productCategoryId: string;
  /**
   * The product status (e.g., 'SCRAPE_STARTED' during refresh)
   *
   * @type {PdpStatus | null | undefined}
   */
  status?: PdpStatus | null;
  /**
   * Product summary data
   */
  summary: ProductSummaryData;
  /**
   * The function to call when the asset is clicked.
   *
   * Callers pass the asset's `imageType` so the handler can route to the
   * default tab for that kind of asset (e.g. a hero asset opens directly on
   * the Hero Score tab instead of bouncing through Image Analysis).
   */
  onAssetClick: (asset: { id: string; imageType: ImageType }) => void;
  /**
   * The function to call when the product details fail to load
   *
   * @param error - The error that occurred
   */
  onAuthenticationError?: (error: unknown | APIError) => void;
  /**
   * Handler for reordering carousel assets by Vizit score
   */
  onCarouselReorderByScore: (onSettle: () => void) => Promise<void>;
  /**
   * Handler for drawer state changes (updates URL)
   */
  onDrawerStateChange?: (isOpen: boolean) => void;
  /**
   * Function to invalidate and poll penalty queries after asset changes
   */
  onPenaltyQueriesInvalidate: () => Promise<void>;
  /**
   * Handler for tab changes (updates URL)
   */
  onTabChange?: (tab: 'hero' | 'carousel') => void;
  /**
   * Handler for carousel source selection changes (updates URL)
   */
  onCarouselSubTabChange?: (tab: AssetsFilterSource) => void;
  /**
   * Handler for hero source selection changes (updates URL)
   */
  onHeroSubTabChange?: (tab: AssetsFilterSource) => void;
  /**
   * Handler for the hero favorites filter toggle (updates URL)
   */
  onHeroFavoritesOnlyChange?: (favoritesOnly: boolean) => void;
  /**
   * Handler for the carousel favorites filter toggle (updates URL)
   */
  onCarouselFavoritesOnlyChange?: (favoritesOnly: boolean) => void;
  /**
   * Current view mode (experiment or original)
   */
  viewMode: ViewMode;
  /**
   * Handler for view mode changes (updates URL)
   */
  onViewModeChange?: (mode: ViewMode) => void;
  /**
   * Original PDP snapshot data for before/after comparison
   */
  originalSnapshot: OriginalPdpSnapshotView | undefined;
  /**
   * Whether the original snapshot is available (not 404, not loading, has data)
   */
  isOriginalAvailable: boolean;
  /**
   * Whether the current view is showing the original PDP snapshot
   * True when viewMode === ORIGINAL and originalSnapshot data is available
   */
  isOriginalView: boolean;
  /**
   * The date from the original snapshot (scraped_at) for display purposes
   * Only available when isOriginalView is true
   */
  originalSnapshotDate: Date;
}

const ProductDetailsContext = createContext<ProductDetailsContextType | null>(null);

/**
 * Hook to access the product details context
 *
 * @returns The product details context data
 * @throws Error if used outside of ProductDetailsContext
 */
const useProductDetails = () => {
  const context = useContext(ProductDetailsContext);

  if (!context) {
    throw new Error('useProductDetails must be used within a ProductDetailsContext');
  }

  return context;
};

export { ProductDetailsContext, useProductDetails, ViewMode };
export type {
  ProductDetailsContextType,
  ProductSummaryData,
  HeroImageData,
  CarouselData,
  OptimizationsData,
  PendingState,
};
